import React from 'react';
import PropTypes from 'prop-types';
import { Layout, Tooltip } from 'antd';
import { translate } from 'react-i18next';
import i18next from 'i18next';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { get } from 'lodash';

import SelectField from 'Parts/ui/components/fields/SelectField';
import SearchBar from 'Parts/search/SearchBarContainer';
import { Link } from 'react-router-dom';
import PaginationTop from 'Parts/search/PaginationTop';
import ResultTable from 'Parts/search/ResultTable';
import Can from 'Parts/session/components/CanContainer';

import { DEFAULT_PAGER } from 'Parts/search/searchUtils';
import { simpleColumn, dateColumn, actionColumn } from 'Parts/common/columnUtils';
import { moneyFormat } from 'Parts/common/moneyFormat';

import transactionsListPageEN from './locales/TransactionsListPage_en.json';
import transactionsListPageFR from './locales/TransactionsListPage_fr.json';

class TransactionsListPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      pager: {
        ...DEFAULT_PAGER,
        total: props.pagination.totalResults || 0,
        current: props.pagination.pageNumber || 1,
        pageSize: props.pagination.pageResults || 10
      },
      dateRange: "all"
    };

    i18next.addResourceBundle('en', 'TransactionsListPage', transactionsListPageEN);
    i18next.addResourceBundle('fr', 'TransactionsListPage', transactionsListPageFR);
  }

  componentDidUpdate(prevProps) {
    // Update the pager with the new total
    let pager;

    if (
      prevProps.pagination.totalResults !== this.props.pagination.totalResults ||
      prevProps.pagination.pageNumber !== this.props.pagination.pageNumber ||
      prevProps.pagination.pageResults !== this.props.pagination.pageResults
    ) {
      pager = {
        ...this.state.pager,
        total: this.props.pagination.totalResults || 0,
        current: this.props.pagination.pageNumber || 1,
        pageSize: this.props.pagination.pageResults || 10
      };

      this.setState({ pager });
    }
  }

  componentWillUnmount() {
    this.props.clearSearch();
  }

  handlePaginationChange = pager => {
    this.setState({ pager });
    const pagination = {
      pageResults: pager.pageSize,
      pageNumber: pager.current
    };
    this.props.searchTransactions(
      this.props.platformId,
      this.props.search,
      this.props.filters,
      pagination,
      { orderBy: 'addedOn[desc]' },
      this.state.dateRange,
      true
    );
  };

  handleViewHandler = transaction => {
    const { history, locale } = this.props;
    if (transaction.scope.type === "platform") {
      return history.push(`/${locale}/d/transactions/${transaction.id}`);
    } else {
      return history.push(
        `/${locale}/d/campaigns/${transaction.scope.path.campaign.id}/transactions/${transaction.id}`,
        { originIsAll: true }
      );
    }
  };

  handleSearch = filters => {
    const pagination = {
      pageResults: this.state.pager.pageSize
    };
    this.props.searchTransactions(
      this.props.platformId,
      filters.search,
      filters,
      pagination,
      { orderBy: 'addedOn[desc]' },
      this.state.dateRange,
      true
    );
  };

  removeParentIdFilterForCSVReport = filters => {
    let _filters = {...filters};
    delete _filters.parentId;
    return _filters;
  }

  startCsvGeneration = () => {
    this.props.reportTransactions(this.props.platformId, this.removeParentIdFilterForCSVReport(this.props.filters),
    this.props.pagination, { orderBy: 'addedOn[desc]' }, this.state.dateRange)
  }

  handleDateRangeChange = dateRange => {
    const pagination = {
      pageResults: this.state.pager.pageSize
    };

    this.setState({ dateRange });

    this.props.searchTransactions(
      this.props.platformId,
      this.props.filters.search,
      this.props.filters,
      pagination,
      { orderBy: 'addedOn[desc]' },
      dateRange,
      true
    );
  }

  render() {
    const {
      t,
      transactions,
      locale,
      pagination,
      reportTransactions,
      platformId,
      checkAuthorization,
      form
    } = this.props;
    const { pager } = this.state;
    const CsvReportButtonDisabled = !transactions || transactions.length == 0;
    const tooltipVisibility = !CsvReportButtonDisabled ? { visible: false } : {};

    let dateRangeOptions = [{
      value: 'all',
      text: t('All Transactions')
    }, {
      value: "Last 4 months",
      text: t('Last 4 Months')
    }, {
      value: "Last 6 months",
      text: t('Last 6 Months')
    }, {
      value: "Last year",
      text: t("Last Year")
    }]

    return (
      <Layout className="TransactionsListPage">
        <Helmet>
          <title>{t('Transactions')}</title>
        </Helmet>
        <SearchBar
          title={t('Transactions')}
          placeholder={t('Search')}
          buttons={[
            <div className="line-zone">
              <SelectField
                form={form}
                fieldId="dateRangeSelect"
                className="TextAreaMultiLangSelect"
                optionsArray={dateRangeOptions}
                initialValue={"all"}
                onChange={this.handleDateRangeChange}
                dropdownClassName="CreatePersonalizationForm__Dropdown"
              />
              <Tooltip title={t('no-transaction')} {...tooltipVisibility}>
                <div className="height-42">
                  <button
                    className="CsvReportButton ant-btn height-42"
                    key={1}
                    disabled={CsvReportButtonDisabled}
                    onClick={this.startCsvGeneration}
                  >
                    <span>{t('CSV')}</span>
                    <FontAwesomeIcon icon={["fal", "file-download"]} />
                  </button>
                </div>
              </Tooltip>
            </div>
          ]}
          createButton={
            (checkAuthorization({ platform: platformId }, [
              'permissions.platform.donation.offline'
            ]) && (
              <div className="column-center">
                <Link to={`/${locale}/d/transactions/add`} className="AddTransactionButton ant-btn ant-btn-primary height-42">
                  <span>{t('Add a transaction')}</span> <FontAwesomeIcon icon={faPlus} />
                </Link>
              </div>
            )) ||
            null
          }
          onSearch={this.handleSearch}
        />
        <Can rules={['permissions.platform.transaction.get']} redirect={`/${locale}/d`}>
          <div className="AdminList">
            <PaginationTop
              totalLabel={total => t('donationsTotal', { count: pagination.totalResults })}
              pager={pager}
              onChange={this.handlePaginationChange}
            />
            <ResultTable
              dataSource={transactions || []}
              pagination={pager}
              onChange={this.handlePaginationChange}
              showHeader={true}
              scroll={{ x: true }}
              columns={[
                simpleColumn(t('Transaction ID'), 'referenceId', null, true),
                simpleColumn(t('Donor name'), 'information', info => {
                  return get(info, 'organization.name') ||
                    get(info, 'firstName') && get(info, 'lastName')
                      ? info.firstName + " " + info.lastName
                      : '';
                }, false),
                simpleColumn(t('Email address'), 'information.email', null, false),
                simpleColumn(t('Recipient'), 'scope.name', null, true),
                simpleColumn(
                  t('Contribution amount'),
                  null,
                  transaction => {
                    const sub = get(transaction, 'subscription.amount', null);
                    return moneyFormat(sub || transaction.amount, locale) + (sub ? '/m' : '');
                  },
                  true
                ),
                simpleColumn(t('Status'), 'status', status => t(status), true),
                dateColumn(t('Date'), 'date', null, null, false, locale),
                actionColumn(t('Edit'), ['fal', 'edit'], this.handleViewHandler)
              ]}
            />
          </div>
        </Can>
      </Layout>
    );
  }
}

TransactionsListPage.propTypes = {
  searchTransactions: PropTypes.func.isRequired,
  transactions: PropTypes.array.isRequired,
  reportTransactions: PropTypes.func.isRequired
};

TransactionsListPage.defaultProps = {
  transactions: [],
  pagination: {
    totalResults: 0
  },
  reportTransactions: null
};

export default translate('TransactionsListPage')(TransactionsListPage);
